/**
 * Simulateur paramétrable, dix hypothèses ajustables.
 *
 * Ce module est consommé **à l'identique** par le serveur et par le
 * navigateur : la simulation présentée en séance et le calcul de l'API sont
 * rigoureusement le même code (spécification § 3).
 *
 * L'exercice en cours n'est pas piloté par les curseurs : il reste figé sur la
 * projection de clôture.
 */
import { AUDIENS, BORNES_SIMULATEUR, DEFAUTS_SIMULATEUR, EXERCICES_TRAJECTOIRE } from './configuration.js';
import { sommeExacte, effectifsInterpoles } from './adherents.js';
import { construireTrajectoire } from './scenarios.js';
/** Paramètres par défaut du simulateur. */
export function parametresParDefaut() {
    return { ...DEFAUTS_SIMULATEUR };
}
function borner(v, b) {
    return Math.min(b.max, Math.max(b.min, v));
}
/**
 * Ramène des paramètres arbitraires dans les bornes déclarées.
 * Toute valeur non finie est remplacée par son défaut : l'interface ne doit
 * jamais pouvoir afficher `NaN`.
 */
export function normaliserParametres(p) {
    const d = parametresParDefaut();
    const num = (v, def, b) => typeof v === 'number' && Number.isFinite(v) ? borner(v, b) : def;
    const exercice = p.exerciceSubvention;
    const exerciceValide = exercice === 'all' ||
        (typeof exercice === 'number' && EXERCICES_TRAJECTOIRE.includes(exercice))
        ? exercice
        : d.exerciceSubvention;
    return {
        cible: Math.round(num(p.cible, d.cible, BORNES_SIMULATEUR.cible)),
        cotisationMoyenne: num(p.cotisationMoyenne, d.cotisationMoyenne, BORNES_SIMULATEUR.cotisationMoyenne),
        dgca: num(p.dgca, d.dgca, BORNES_SIMULATEUR.dgca),
        fcapCcneac: num(p.fcapCcneac, d.fcapCcneac, BORNES_SIMULATEUR.fcapCcneac),
        fcapCcnsvp: num(p.fcapCcnsvp, d.fcapCcnsvp, BORNES_SIMULATEUR.fcapCcnsvp),
        audiens: num(p.audiens, d.audiens, BORNES_SIMULATEUR.audiens),
        subventionPonctuelle: num(p.subventionPonctuelle, d.subventionPonctuelle, BORNES_SIMULATEUR.subventionPonctuelle),
        exerciceSubvention: exerciceValide,
        progressionCharges: num(p.progressionCharges, d.progressionCharges, BORNES_SIMULATEUR.progressionCharges),
        ajustement: num(p.ajustement, d.ajustement, BORNES_SIMULATEUR.ajustement),
    };
}
/** Trajectoire produite par un jeu de paramètres. */
export function simuler(p, e) {
    const annees = e.exercices ?? EXERCICES_TRAJECTOIRE;
    const nbPas = annees.length - 1;
    const adherents = effectifsInterpoles(e.payeursExerciceCourant, p.cible, nbPas);
    const produits = [e.produitsExerciceCourant];
    const charges = [e.chargesExerciceCourant];
    for (let i = 1; i <= nbPas; i++) {
        const annee = annees[i];
        const audiens = annee <= AUDIENS.derniereAnnee ? p.audiens : 0;
        const ponctuelle = p.subventionPonctuelle > 0 &&
            (p.exerciceSubvention === 'all' || p.exerciceSubvention === annee)
            ? p.subventionPonctuelle
            : 0;
        produits.push(sommeExacte([
            adherents[i] * p.cotisationMoyenne,
            p.dgca,
            p.fcapCcneac,
            p.fcapCcnsvp,
            audiens,
            ponctuelle,
        ]));
        charges.push(sommeExacte([e.chargesExerciceCourant * (1 + p.progressionCharges) ** i, p.ajustement]));
    }
    return construireTrajectoire({
        cle: 'sim',
        libelle: 'Simulateur',
        annees,
        adherents,
        produits,
        charges,
        reserveDepart: e.reserveDepart,
        ...(e.seuils ? { seuils: e.seuils } : {}),
    });
}
/**
 * Message de lecture accompagnant une simulation. Le texte est produit ici et
 * non dans le gabarit : il dépend du résultat du calcul.
 */
export function messageSimulation(t, p, reserveExerciceCourant) {
    const variables = {
        cible: p.cible,
        cotisationMoyenne: p.cotisationMoyenne,
        reserveFinale: t.reserveFinale,
        ecartFin2026: sommeExacte([t.reserveFinale, -reserveExerciceCourant]),
    };
    if (t.franchissementSeuil !== null) {
        return { ton: 'negative', cle: 'seuil_franchi', variables: { ...variables, annee: t.franchissementSeuil } };
    }
    if (t.retourEquilibre !== null) {
        return { ton: 'positive', cle: 'equilibre', variables: { ...variables, annee: t.retourEquilibre } };
    }
    return { ton: 'warning', cle: 'deficitaire_soutenable', variables };
}
//# sourceMappingURL=simulateur.js.map